import { chatApi } from "./chatClient";
import type { Conversation, ConversationMember, CursorPage } from "./types";

export interface ListConversationsParams {
  cursor?: string | null;
  limit?: number;
}

export interface CreateConversationBody {
  kind: "dm" | "group_dm" | "space";
  // For DMs:
  recipient_user_sub?: string;
  // For group DMs:
  participant_user_subs?: string[];
  // For spaces:
  name?: string;
  description?: string;
  topic?: string;
  access?: "restricted" | "org_wide" | "discoverable";
  is_announcement?: boolean;
}

export interface UpdateConversationBody {
  name?: string;
  description?: string;
  topic?: string;
  access?: "restricted" | "org_wide" | "discoverable";
  is_announcement?: boolean;
}

function qs(params: Record<string, string | number | undefined | null>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== "",
  ) as [string, string | number][];
  return entries.length ? `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)]))}` : "";
}

export const conversationsService = {
  list: (params: ListConversationsParams = {}) =>
    chatApi.get<CursorPage<Conversation>>(`/conversations/${qs(params as Record<string, string | number | undefined | null>)}`),

  retrieve: (id: string) => chatApi.get<Conversation>(`/conversations/${id}/`),

  create: (body: CreateConversationBody) =>
    chatApi.post<Conversation>(`/conversations/`, body),

  update: (id: string, body: UpdateConversationBody) =>
    chatApi.patch<Conversation>(`/conversations/${id}/`, body),

  archive: (id: string) =>
    chatApi.post<Conversation>(`/conversations/${id}/archive/`),

  join: (id: string) =>
    chatApi.post<Conversation>(`/conversations/${id}/join/`),

  // Owner succession (#1619 AC43-AC47). `successorUserId` carries the nominee's
  // `sub` — the same identifier `addMember`/`changeMemberRole` speak. It is
  // omitted entirely rather than sent as null when there is no nomination: the
  // server treats a missing key and an empty one alike, but sending the key on
  // every leave would invite a reader to think succession is always in play.
  leave: (id: string, successorUserId?: string) =>
    chatApi.post<void>(
      `/conversations/${id}/leave/`,
      successorUserId ? { successor_user_id: successorUserId } : undefined,
    ),

  // "Remove from list" (#1619 AC38-AC42). Hides a conversation the caller has
  // already left; it deletes nothing, and the server refuses it (422
  // `conversation_not_frozen`) on a conversation the caller is still in.
  dismiss: (id: string) =>
    chatApi.post<void>(`/conversations/${id}/dismiss/`),

  discover: (params: { search?: string; cursor?: string | null; limit?: number } = {}) =>
    chatApi.get<CursorPage<Conversation>>(`/spaces/discover/${qs(params as Record<string, string | number | undefined | null>)}`),

  // Toggles the caller's own notification_level between "off" and "all".
  // Self-service — works on any conversation kind, including a DM, which has
  // no owner to gate a PATCH the way member-role changes are.
  mute: (id: string) =>
    chatApi.post<ConversationMember>(`/conversations/${id}/mute/`),

  // Members
  listMembers: (conversationId: string) =>
    chatApi.get<ConversationMember[]>(`/conversations/${conversationId}/members/`),

  addMember: (conversationId: string, body: { user_sub: string; role?: "owner" | "manager" | "member" }) =>
    chatApi.post<ConversationMember>(`/conversations/${conversationId}/members/`, body),

  changeMemberRole: (conversationId: string, userSub: string, role: "owner" | "manager" | "member") =>
    chatApi.patch<ConversationMember>(`/conversations/${conversationId}/members/${userSub}/`, { role }),

  removeMember: (conversationId: string, userSub: string) =>
    chatApi.delete<void>(`/conversations/${conversationId}/members/${userSub}/`),
};
